import { Button, Flex, HStack, Text, useToken, VStack } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import type { PlanningDailyViewUserByDayModel } from "@src/components/modules/resource-planning/timeline/models";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import {
  formatCapacity,
  getCapacityStripState,
  getCapacityTexts,
} from "@src/components/modules/resource-planning/timeline/UserCapacityStripForDay";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import Color from "color";
import { observer } from "mobx-react-lite";
import type { FC } from "react";

type AllocateCapacityColumnAreaProps = {
  day: PlanningDailyViewUserByDayModel["items"][0];
  user: PlanningDailyViewUserByDayModel["user"];
};

export const AllocateCapacityColumnArea: FC<AllocateCapacityColumnAreaProps> =
  observer(function AllocateCapacityColumnArea({ user, day }) {
    const { capacity, workStatus } = day;
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const { allocationModalStore } = useStore();
    const [jade100, red200] = useToken("colors", ["jade.100", "red.200"]);
    const { isDesktop } = useScreenType();

    return can("planning_create_all") ||
      can({ do: "planning_create_own", eval: ({ id }) => id === user.id }) ? (
      <Flex flex="1" role="group">
        <Button
          alignItems="center"
          justifyContent="center"
          w="full"
          minH="80px"
          bg={Color(capacity && capacity > 0 ? jade100 : red200)
            .alpha(0.3)
            .toString()}
          opacity={isDesktop ? "0" : undefined}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor={`${capacity && capacity > 0 ? "jade" : "red"}.500`}
          _groupHover={{
            opacity: resourcePlanningStore.active ? 0 : 1,
          }}
          onClick={() =>
            allocationModalStore.modalState.onOpen({
              userId: user.id,
              date: new Date(day.date),
              onSubmit: () =>
                resourcePlanningStore.fetchDailyView({
                  loadForAllUsers: true,
                  backgroundUpdate: false,
                }),
            })
          }
          rounded="lg"
          variant="unstyled"
        >
          <VStack>
            <HStack
              justifyContent="center"
              flexWrap="wrap"
              display="inline-flex"
              mx="2"
              px="3"
              py="2"
              bg="white"
              borderWidth="1px"
              borderColor="grey.100"
              rounded="lg"
            >
              <Icon name="plus" w="4" h="4" color="grey.600" />
              <Text fontSize="sm" fontWeight="semibold">
                <Trans>Allocate capacity</Trans>
              </Text>
            </HStack>
            <Text
              color={`${capacity && capacity > 0 ? "jade" : "red"}.800`}
              fontSize="xs"
              fontWeight="semibold"
            >
              {formatCapacity(capacity, workStatus)}{" "}
              {getCapacityTexts()[getCapacityStripState(workStatus, capacity)]}
            </Text>
          </VStack>
        </Button>
      </Flex>
    ) : null;
  });
